import { useEffect, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link, useParams } from 'react-router-dom';
import Dashboard from './pages/Dashboard';
import Overview from './pages/overview';
import Categories from './pages/tasks/Categories';
import Subcategories from './pages/tasks/Subcategories';
import ItemTypes from './pages/tasks/ItemTypes';
import Approvals from './pages/approvals';
import VideoTrimmingPage from './pages/VideoTrimming';
import QA from './pages/qa';
import DatasetRequests from './pages/DatasetRequests';
import SupportRequests from './pages/SupportRequests';
import AndroidTesters from './pages/AndroidTesters';
import Payouts from './pages/Payouts';
import Analytics from './pages/analytics';
import RejectionAnalytics from './pages/rejection-analytics';
import OrphanSubmissions from './pages/OrphanSubmissions';
import UserData from './pages/user-data';
import SchemaNeeded from './pages/labeling/Queue';
import DetectionSetup from './pages/labeling/DetectionSetup';
import AttributeManager from './pages/labeling/AttributeManager';
import Workbench from './pages/labeling/Workbench';
import WorkbenchList from './pages/labeling/WorkbenchList';
import WorkbenchEfficient from './pages/labeling/WorkbenchEfficient';
import VideoWorkbench from './pages/labeling/VideoWorkbench';
import QCReview from './pages/labeling/QCReview';
import Review from './pages/labeling/Review';
import Datasets from './pages/labeling/Datasets';
import DetectionSnapshots from './pages/labeling/DetectionSnapshots';
import Export from './pages/admin/Export';
import ModelQAPage from './pages/ModelQA';
import ModelQATaskSelector from './pages/ModelQA/TaskSelector';
import ActiveLearningPage from './pages/ActiveLearning';
import ActiveLearningTaskSelector from './pages/ActiveLearning/TaskSelector';
import ModelsPage from './pages/Models';
// OLD SCHEMA SYSTEM - DISABLED TO PREVENT CONFUSION
// import LabelSchemasList from './pages/admin/label-schemas/index';
// import LabelSchemaEditor from './pages/admin/label-schemas/[schemaId]';
import TaskEditor from './pages/admin/tasks/[taskId]';
import WorkforceDashboard from './pages/admin/Workforce';
import UserManagement from './pages/admin/UserManagement';
import UserSettings from './pages/UserSettings/index';
import ReferenceImages from './pages/ReferenceImages';
import ChallengesList from './pages/challenges/ChallengesList';
import ChallengeEditor from './pages/challenges/ChallengeEditor';
import ContestsList from './pages/contests/ContestsList';
import ContestEditor from './pages/contests/ContestEditor';
import CreditPackages from './pages/client-portal/CreditPackages';
import DatasetPackagesPage from './pages/client/DatasetPackages';
import PhotoPackagesPage from './pages/client/PhotoPackages';
import VideoPackagesPage from './pages/client/VideoPackages';
import TaskPricingPage from './pages/client/TaskPricing';
// import WorkbenchList from './pages/labeling/WorkbenchList';
// import Workbench from './pages/labeling/Workbench';
// import ReviewList from './pages/labeling/ReviewList';
// import ReviewDetail from './pages/labeling/ReviewDetail';
// import DatasetsList from './pages/labeling/DatasetsList';
// import DatasetCreateWizard from './pages/labeling/DatasetCreateWizard';
// import DatasetDetail from './pages/labeling/DatasetDetail';
// import ActiveLearningPolicies from './pages/labeling/ActiveLearningPolicies';
// import ActiveLearningPolicyForm from './pages/labeling/ActiveLearningPolicyForm';
// import ModelQADashboard from './pages/labeling/ModelQADashboard';
// import ModelsList from './pages/labeling/ModelsList';
// import ModelRegister from './pages/labeling/ModelRegister';
// import ProductsList from './pages/labeling/sales/ProductsList';
// import ProductEditor from './pages/labeling/sales/ProductEditor';
// import OrdersList from './pages/labeling/sales/OrdersList';
// import EntitlementsList from './pages/labeling/sales/EntitlementsList';
// import StoreFront from './pages/store/StoreFront';
// import ProductPage from './pages/store/ProductPage';
import Sidebar from "./components/Sidebar";
import Login from './pages/login';
import { ENABLE_SALES, ENABLE_PUBLIC_STORE } from './config/flags';

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      const token = await user.getIdTokenResult(true);
      setIsAdmin(!!token.claims.admin);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  if (loading) return <div>Loading...</div>;
  if (!isAdmin) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

function RedirectToUnified({ type }: { type: 'workbench' | 'qc' }) {
  const { jobId } = useParams<{ jobId: string }>();
  return <Navigate to={`/labeling/${jobId}`} replace />;
}

function AppLayout() {
  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-8 bg-white min-h-screen">
        <Routes>
          <Route path="/" element={<RequireAdmin><Navigate to="/dashboard" /></RequireAdmin>} />
          <Route path="/dashboard" element={<RequireAdmin><Dashboard /></RequireAdmin>} />
          <Route path="/tasks" element={<RequireAdmin><Overview /></RequireAdmin>} />
          <Route path="/tasks/categories" element={<RequireAdmin><Categories /></RequireAdmin>} />
          <Route path="/tasks/subcategories" element={<RequireAdmin><Subcategories /></RequireAdmin>} />
          <Route path="/tasks/item-types" element={<RequireAdmin><ItemTypes /></RequireAdmin>} />
          <Route path="/challenges" element={<RequireAdmin><ChallengesList /></RequireAdmin>} />
          <Route path="/challenges/:id" element={<RequireAdmin><ChallengeEditor /></RequireAdmin>} />
          <Route path="/contests" element={<RequireAdmin><ContestsList /></RequireAdmin>} />
          <Route path="/contests/:id" element={<RequireAdmin><ContestEditor /></RequireAdmin>} />
          <Route path="/approvals" element={<RequireAdmin><Approvals /></RequireAdmin>} />
          <Route path="/video-trimming" element={<RequireAdmin><VideoTrimmingPage /></RequireAdmin>} />
          <Route path="/analytics" element={<RequireAdmin><Analytics /></RequireAdmin>} />
          <Route path="/rejection-analytics" element={<RequireAdmin><RejectionAnalytics /></RequireAdmin>} />
          <Route path="/user-data" element={<RequireAdmin><UserData /></RequireAdmin>} />
        <Route path="/schema-needed" element={<RequireAdmin><SchemaNeeded /></RequireAdmin>} />
          <Route path="/orphan-submissions" element={<RequireAdmin><OrphanSubmissions /></RequireAdmin>} />
                      <Route path="/labeling/schemas" element={<RequireAdmin><DetectionSetup /></RequireAdmin>} />
                      <Route path="/labeling/attributes" element={<RequireAdmin><AttributeManager /></RequireAdmin>} />
                              <Route path="/labeling/workbench" element={<RequireAdmin><WorkbenchEfficient /></RequireAdmin>} />
        <Route path="/labeling/video-workbench" element={<RequireAdmin><VideoWorkbench /></RequireAdmin>} />
        <Route path="/labeling/workbench-old" element={<RequireAdmin><WorkbenchList /></RequireAdmin>} />
        <Route path="/labeling/:id" element={<RequireAdmin><Workbench /></RequireAdmin>} />
        <Route path="/labeling/workbench/:submissionId" element={<RequireAdmin><Workbench /></RequireAdmin>} />
        <Route path="/labeling/workbench/:jobId" element={<RequireAdmin><RedirectToUnified type="workbench" /></RequireAdmin>} />
        <Route path="/labeling/qc/:jobId" element={<RequireAdmin><RedirectToUnified type="qc" /></RequireAdmin>} />
        <Route path="/labeling/review" element={<RequireAdmin><Review /></RequireAdmin>} />
        <Route path="/labeling/review/:jobId" element={<RequireAdmin><QCReview /></RequireAdmin>} />
        <Route path="/qa" element={<RequireAdmin><QA /></RequireAdmin>} />
        <Route path="/dataset-requests" element={<RequireAdmin><DatasetRequests /></RequireAdmin>} />
        <Route path="/support-requests" element={<RequireAdmin><SupportRequests /></RequireAdmin>} />
        <Route path="/android-testers" element={<RequireAdmin><AndroidTesters /></RequireAdmin>} />
        <Route path="/payouts" element={<RequireAdmin><Payouts /></RequireAdmin>} />
        <Route path="/labeling/datasets" element={<RequireAdmin><Datasets /></RequireAdmin>} />
        <Route path="/labeling/detection-snapshots" element={<RequireAdmin><DetectionSnapshots /></RequireAdmin>} />
        <Route path="/labeling/datasets/new" element={<RequireAdmin><div><h1>New Dataset</h1><p>New dataset functionality coming soon...</p></div></RequireAdmin>} />
        <Route path="/labeling/datasets/:datasetId" element={<RequireAdmin><div><h1>Dataset Detail</h1><p>Dataset detail functionality coming soon...</p></div></RequireAdmin>} />
        <Route path="/labeling/active" element={<RequireAdmin><ActiveLearningTaskSelector /></RequireAdmin>} />
        <Route path="/labeling/active/:taskId" element={<RequireAdmin><ActiveLearningPage /></RequireAdmin>} />
        <Route path="/admin/export" element={<RequireAdmin><Export /></RequireAdmin>} />
        {/* OLD SCHEMA SYSTEM - DISABLED TO PREVENT CONFUSION */}
        {/* <Route path="/admin/label-schemas" element={<RequireAdmin><LabelSchemasList /></RequireAdmin>} /> */}
        {/* <Route path="/admin/label-schemas/new" element={<RequireAdmin><LabelSchemaEditor /></RequireAdmin>} /> */}
        {/* <Route path="/admin/label-schemas/:schemaId" element={<RequireAdmin><LabelSchemaEditor /></RequireAdmin>} /> */}
        {/* Option B: Overview (/tasks) is the only task editor. Keep TaskEditor file but make it unreachable. */}
        <Route path="/admin/tasks/*" element={<RequireAdmin><Navigate to="/tasks" replace /></RequireAdmin>} />
        <Route path="/admin/workforce" element={<RequireAdmin><WorkforceDashboard /></RequireAdmin>} />
        <Route path="/admin/user-management" element={<RequireAdmin><UserManagement /></RequireAdmin>} />
        <Route path="/user-settings" element={<RequireAdmin><UserSettings /></RequireAdmin>} />
        <Route path="/labeling/model-qa" element={<RequireAdmin><ModelQATaskSelector /></RequireAdmin>} />
        <Route path="/labeling/model-qa/:taskId" element={<RequireAdmin><ModelQAPage /></RequireAdmin>} />
        <Route path="/labeling/models" element={<RequireAdmin><ModelsPage /></RequireAdmin>} />
        <Route path="/labeling/models/new" element={<RequireAdmin><div><h1>New Model</h1><p>New model functionality coming soon...</p></div></RequireAdmin>} />
        <Route path="/reference-images" element={<RequireAdmin><ReferenceImages /></RequireAdmin>} />
        <Route path="/client-portal/packages" element={<RequireAdmin><CreditPackages /></RequireAdmin>} />
        <Route path="/client/dataset-packages" element={<RequireAdmin><DatasetPackagesPage /></RequireAdmin>} />
        <Route path="/client/photo-packages" element={<RequireAdmin><PhotoPackagesPage /></RequireAdmin>} />
        <Route path="/client/video-packages" element={<RequireAdmin><VideoPackagesPage /></RequireAdmin>} />
        <Route path="/client/task-pricing" element={<RequireAdmin><TaskPricingPage /></RequireAdmin>} />
        {/* <Route path="/labeling/models" element={<RequireAdmin><ModelsList /></RequireAdmin>} />
        <Route path="/labeling/models/new" element={<RequireAdmin><ModelRegister /></RequireAdmin>} />
        {ENABLE_SALES && (
          <>
            <Route path="/labeling/sales/products" element={<RequireAdmin><ProductsList /></RequireAdmin>} />
            <Route path="/labeling/sales/products/new" element={<RequireAdmin><ProductEditor /></RequireAdmin>} />
            <Route path="/labeling/sales/products/:productId" element={<RequireAdmin><ProductEditor /></RequireAdmin>} />
            <Route path="/labeling/sales/orders" element={<RequireAdmin><OrdersList /></RequireAdmin>} />
            <Route path="/labeling/sales/entitlements" element={<RequireAdmin><EntitlementsList /></RequireAdmin>} />
          </>
        )}
        {ENABLE_PUBLIC_STORE && (
          <>
            <Route path="/store" element={<StoreFront />} />
            <Route path="/store/:productId" element={<ProductPage />} />
          </>
        )} */}
        {/* Redirect sales routes when disabled */}
        {!ENABLE_SALES && (
          <>
            <Route path="/labeling/sales/*" element={<Navigate to="/tasks" replace />} />
          </>
        )}
        {!ENABLE_PUBLIC_STORE && (
          <>
            <Route path="/store/*" element={<Navigate to="/tasks" replace />} />
            <Route path="/download/*" element={<Navigate to="/tasks" replace />} />
          </>
        )}
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={<AppLayout />} />
      </Routes>
    </Router>
  );
}

export default App;
